/**
 * Integrytas Group - UI Layout Delta Override Engine (ULD)
 * uld_merge - the pure runtime layout merger.
 *
 * Applies an ordered delta array onto a code-provided core layout. Unknown
 * component keys (a field the latest release removed/renamed) are skipped via the
 * onMissing callback rather than breaking the render. Pure + deterministic.
 *
 * Two entry points share the same field-merge core (`mergeFields`):
 *   - `applyLayoutDelta`   → a single flat ordered list (single-section pages).
 *   - `applySectionedLayout` → fields grouped into ordered sections (multi-section
 *     pages); supports cross-section moves (`set_section`) and section reordering
 *     (`reorder_section`).
 */

export interface UldLayoutItem {
  key: string;
  label?: string;
  tab?: string | null;
  section?: string | null;
  col_span?: number;
  visible?: boolean;
  order?: number;
  required?: boolean;   // form-field "required" personalization
  readonly?: boolean;   // form-field "read-only" personalization
  width?: number;       // grid-column width personalization
  [extra: string]: unknown;
}

export type UldActionType =
  | 'hide_element' | 'reorder_index' | 'relabel_component'
  | 'assign_field_to_tab' | 'set_col_span' | 'set_section'
  | 'reorder_section' | 'set_required' | 'set_readonly' | 'set_col_width' | 'set_visible';

export interface UldDeltaAction {
  action: UldActionType;
  /** Field key for field actions; section key for `reorder_section`. */
  component_key: string;
  index?: number;
  label?: string;
  tab?: string;
  section?: string;
  col_span?: number;
  required?: boolean;
  readonly?: boolean;
  width?: number;
  visible?: boolean;
}

/** Declares a section's identity + header label and (by array order) its default order. */
export interface SectionDef {
  key: string;
  label?: string;
}

export interface UldMergeResult {
  layout: UldLayoutItem[];
  /** Actions skipped because their component_key isn't in the core layout. */
  missing: UldDeltaAction[];
}

export interface UldSection {
  key: string;
  label?: string;
  order: number;
  /** All items in this section (hidden ones retain `visible:false`), ordered. */
  items: UldLayoutItem[];
}

export interface UldSectionedResult {
  sections: UldSection[];
  missing: UldDeltaAction[];
}

const clampSpan = (n: number) => Math.min(Math.max(n, 1), 12);

/**
 * Apply the field-scoped actions onto a clone of `core`. Returns the items with
 * their effective `order` / `section` / `visible` / etc. (NOT sorted). Section
 * actions (`reorder_section`) are ignored here — they are not field targets.
 */
function mergeFields(
  core: UldLayoutItem[],
  delta: UldDeltaAction[] | null | undefined,
  onMissing?: (action: UldDeltaAction) => void,
): { items: UldLayoutItem[]; missing: UldDeltaAction[] } {
  const items: UldLayoutItem[] = core.map((it, i) => ({ visible: true, ...it, order: it.order ?? i }));
  const byKey = new Map<string, UldLayoutItem>(items.map(it => [it.key, it]));
  const missing: UldDeltaAction[] = [];

  for (const a of delta ?? []) {
    if (a?.action === 'reorder_section') continue; // section-scoped, not a field target
    const target = a && a.component_key ? byKey.get(a.component_key) : undefined;
    if (!target) {
      if (a && a.component_key) { missing.push(a); onMissing?.(a); }
      continue; // collateral-damage: omit silently
    }
    switch (a.action) {
      case 'hide_element':        target.visible = false; break;
      case 'reorder_index':       if (typeof a.index === 'number') target.order = a.index; break;
      case 'relabel_component':   if (a.label != null) target.label = a.label; break;
      case 'assign_field_to_tab': target.tab = a.tab ?? null; break;
      case 'set_section':         target.section = a.section ?? null; break;
      case 'set_col_span':        if (typeof a.col_span === 'number') target.col_span = clampSpan(a.col_span); break;
      case 'set_required':        if (typeof a.required === 'boolean') target.required = a.required; break;
      case 'set_readonly':        if (typeof a.readonly === 'boolean') target.readonly = a.readonly; break;
      case 'set_col_width':       if (typeof a.width === 'number') target.width = a.width; break;
      case 'set_visible':         if (typeof a.visible === 'boolean') target.visible = a.visible; break;
      default: /* unknown action verb — ignore */ break;
    }
  }
  return { items, missing };
}

/**
 * Merge `delta` onto `core`. Returns the effective FLAT layout (ordered) plus the
 * list of skipped actions. `core` is never mutated. Single-section pages.
 */
export function applyLayoutDelta(
  core: UldLayoutItem[],
  delta: UldDeltaAction[] | null | undefined,
  onMissing?: (action: UldDeltaAction) => void,
): UldMergeResult {
  const { items, missing } = mergeFields(core, delta, onMissing);
  // Stable sort by effective order.
  const layout = items
    .map((it, i) => ({ it, i }))
    .sort((x, y) => (x.it.order ?? x.i) - (y.it.order ?? y.i) || x.i - y.i)
    .map(({ it }) => it);
  return { layout, missing };
}

/**
 * Merge `delta` onto `core` and group the result into ordered sections.
 * - Field order uses a GLOBAL absolute `order` space (sorting within a section by
 *   `order` therefore preserves the intended relative order regardless of how
 *   `set_section` reassigned membership).
 * - Section order seeds from `sectionDefs` index, overridden by `reorder_section`.
 * - A field whose effective section is unknown (e.g. its section was removed in a
 *   new release) falls back to its CORE section, else the first section — never dropped.
 * - Hidden items are kept (with `visible:false`) so the design editor can unhide them;
 *   the renderer filters them out.
 */
export function applySectionedLayout(
  core: UldLayoutItem[],
  sectionDefs: SectionDef[],
  delta: UldDeltaAction[] | null | undefined,
  onMissing?: (action: UldDeltaAction) => void,
): UldSectionedResult {
  const { items, missing } = mergeFields(core, delta, onMissing);

  const sectionOrder = new Map<string, number>();
  sectionDefs.forEach((s, i) => sectionOrder.set(s.key, i));
  for (const a of delta ?? []) {
    if (a?.action === 'reorder_section' && a.component_key && typeof a.index === 'number'
        && sectionOrder.has(a.component_key)) {
      sectionOrder.set(a.component_key, a.index);
    }
  }

  const defaultSection = sectionDefs[0]?.key ?? '';
  const coreSection = new Map<string, string | null | undefined>(core.map(c => [c.key, c.section ?? null]));
  const effectiveSection = (it: UldLayoutItem): string => {
    const s = it.section ?? null;
    if (s != null && sectionOrder.has(s)) return s;
    const cs = coreSection.get(it.key) ?? null;
    return (cs != null && sectionOrder.has(cs)) ? cs : defaultSection;
  };

  const groups = new Map<string, { it: UldLayoutItem; i: number }[]>();
  items.forEach((it, i) => {
    const sec = effectiveSection(it);
    let arr = groups.get(sec);
    if (!arr) { arr = []; groups.set(sec, arr); }
    arr.push({ it, i });
  });

  const sections: UldSection[] = sectionDefs
    .map(s => {
      const recs = (groups.get(s.key) ?? []).slice()
        .sort((x, y) => (x.it.order ?? x.i) - (y.it.order ?? y.i) || x.i - y.i);
      return { key: s.key, label: s.label, order: sectionOrder.get(s.key) ?? 0, items: recs.map(r => r.it) };
    })
    .sort((a, b) => a.order - b.order);

  return { sections, missing };
}
